/**
 * Notebooks: quem está inscrito no Campo, quem foi cortado e quem anda calado.
 *
 * **A pergunta que esta tela responde** é "a congregação diz que lançou, e o número não
 * aparece aqui; o notebook dela está mandando?". Por isso a coluna que vem primeiro depois do
 * nome é há quantos dias ele não envia, e não a data em que foi inscrito.
 *
 * **Três ações que antes eram do token de administração**: cortar um notebook perdido,
 * devolver o acesso e gerar o código de um notebook novo. O token é do programador, não do
 * pastor, e um notebook roubado num domingo à noite não pode esperar.
 *
 * **Cortar o acesso é só cortar o acesso.** O que o notebook já mandou continua valendo, e a
 * tela diz isso com essas palavras: aquilo é dinheiro que alguém lançou de verdade.
 */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type CodigoDeInscricao, type Eu, type NotebookNaTela, type TelaDeNotebooks
} from '../api'
import { AbasDaAdministracao } from '../componentes/AbasDaAdministracao'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { quandoFoi } from '../dinheiro'
import { CaixaDoCodigo } from '../componentes/CaixaDoCodigo'

/** `2026-08-05T12:00:00Z` vira `05/08/2026`. */
function dia(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

type EmFoco =
  | { o: 'nada' }
  | { o: 'cortar'; notebook: NotebookNaTela }
  | { o: 'devolver'; notebook: NotebookNaTela }
  | { o: 'inscrever' }

export function Notebooks({ eu, aoSair }: { eu: Eu; aoSair: () => void }): JSX.Element {
  const [dados, setDados] = useState<TelaDeNotebooks | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [foco, setFoco] = useState<EmFoco>({ o: 'nada' })
  const [recado, setRecado] = useState<string | null>(null)
  const [codigo, setCodigo] = useState<CodigoDeInscricao | null>(null)

  const carregar = useCallback(() => {
    setErro(null)
    servidor
      .notebooks()
      .then(setDados)
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })
  }, [aoSair])

  useEffect(carregar, [carregar])

  return (
    <>
      <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: '/', texto: 'Início' }} />

      <main className="folha" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Notebooks</h1>
        <AbasDaAdministracao aquiEstou="/notebooks" />

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}
        {recado !== null && <p className="recado feito" role="status">{recado}</p>}

        {codigo !== null && <OCodigo codigo={codigo} aoFechar={() => setCodigo(null)} />}

        {dados !== null && (
          <>
            <p className="totais">
              <span>{dados.ativos === 1 ? '1 ativo' : `${dados.ativos} ativos`}</span>
              <span>{dados.cortados === 1 ? '1 cortado' : `${dados.cortados} cortados`}</span>
              {dados.de_ensaio > 0 && (
                <span>
                  {dados.de_ensaio === 1 ? '1 de ensaio' : `${dados.de_ensaio} de ensaio`}
                </span>
              )}
            </p>

            {dados.preocupam > 0 && (
              <p className="cartao">
                <span className="titulo">
                  {dados.preocupam === 1
                    ? '1 notebook ativo não envia há mais de uma semana'
                    : `${dados.preocupam} notebooks ativos não enviam há mais de uma semana`}
                </span>
                <span className="detalhe">
                  Pode ser computador com defeito, internet caída ou programa fechado faz tempo.
                </span>
              </p>
            )}

            {/*
              Inscrever é só do administrador máximo: quem gera um código entrega uma cópia
              inteira do Campo ao computador que entrar com ele. Cortar e devolver não entregam
              dado a ninguém novo, e continuam com quem cuida do cadastro do Campo.
            */}
            {dados.posso_inscrever && foco.o !== 'inscrever' && (
              <p className="linha-form">
                <button onClick={() => { setFoco({ o: 'inscrever' }); setCodigo(null) }}>
                  Inscrever um notebook novo
                </button>
              </p>
            )}

            {foco.o === 'inscrever' && (
              <FormularioDeInscrever
                congregacoes={dados.congregacoes}
                aoDesistir={() => setFoco({ o: 'nada' })}
                aoGerar={(gerado) => {
                  setCodigo(gerado)
                  setFoco({ o: 'nada' })
                  carregar()
                }}
                aoSair={aoSair}
              />
            )}

            <ul className="notebooks">
              {dados.notebooks.map((n) => (
                <li key={n.id} className={n.ativo ? undefined : 'cortado'}>
                  <span className="nome">
                    {n.nome}
                    {!n.ativo && <span className="situacao NAO_FECHOU">cortado</span>}
                    {n.de_ensaio && <span className="situacao TRAVADO">de ensaio</span>}
                    {n.preocupa && <span className="situacao NAO_FECHOU">calado</span>}
                  </span>
                  <span className="detalhe">
                    {n.congregacao.nome ?? 'congregação que ainda não chegou ao servidor'} ·{' '}
                    {quandoFoi(n.ultimo_envio)}
                    {n.ultimo_envio !== null && <> ({dia(n.ultimo_envio)})</>}
                    {' '}· inscrito em {dia(n.inscrito_em)}
                  </span>
                  {n.motivo_inativo !== null && (
                    <span className="detalhe">Cortado porque: {n.motivo_inativo}</span>
                  )}

                  {dados.posso_administrar && (
                    <span className="acoes so-tela">
                      {n.ativo ? (
                        <button className="mini perigo" onClick={() => setFoco({ o: 'cortar', notebook: n })}>
                          Cortar o acesso
                        </button>
                      ) : (
                        <button className="mini" onClick={() => setFoco({ o: 'devolver', notebook: n })}>
                          Devolver o acesso
                        </button>
                      )}
                    </span>
                  )}

                  {(foco.o === 'cortar' || foco.o === 'devolver') && foco.notebook.id === n.id && (
                    <FormularioDeMudanca
                      notebook={n}
                      cortando={foco.o === 'cortar'}
                      aoDesistir={() => setFoco({ o: 'nada' })}
                      aoMudar={(novoRecado) => {
                        setRecado(novoRecado)
                        setFoco({ o: 'nada' })
                        carregar()
                      }}
                      aoSair={aoSair}
                    />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  )
}

/**
 * O código de inscrição, mostrado UMA VEZ.
 *
 * O servidor guarda só o hash dele, como faz com senha. Fechar esta caixa sem anotar quer
 * dizer gerar outro, e é melhor assim: um código que se pode reler é um código que fica
 * guardado em algum lugar.
 */
function OCodigo({ codigo, aoFechar }: { codigo: CodigoDeInscricao; aoFechar: () => void }): JSX.Element {
  return (
    <section className="codigo-de-inscricao" role="status">
      <p className="titulo">Código para {codigo.congregacao.nome}</p>
      <p className="codigo">{codigo.codigo}</p>
      <p className="explicacao">{codigo.recado}</p>
      <p className="explicacao atencao">
        Ele aparece <strong>uma vez só</strong>. Anote agora: o servidor guarda só o resumo dele,
        e não tem como mostrar de novo. Se perder, gere outro.
      </p>
      <button onClick={aoFechar}>Já anotei</button>
    </section>
  )
}

/** Cortar ou devolver: motivo escrito e senha, porque os dois mexem no acesso de um Campo. */
function FormularioDeMudanca({
  notebook,
  cortando,
  aoDesistir,
  aoMudar,
  aoSair
}: {
  notebook: NotebookNaTela
  cortando: boolean
  aoDesistir: () => void
  aoMudar: (recado: string) => void
  aoSair: () => void
}): JSX.Element {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function mudar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      const feito = cortando
        ? await servidor.cortarNotebook(notebook.id, motivo, senha, codigo.trim())
        : await servidor.devolverAcesso(notebook.id, motivo, senha, codigo.trim())
      aoMudar(feito.recado)
    } catch (falha: unknown) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível mudar.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void mudar(e)}>
      <p className="recado atencao" role="note">
        {cortando
          ? 'Cortar o acesso é só cortar o acesso. O que este notebook já mandou continua valendo, porque é dinheiro que alguém lançou.'
          : 'O acesso volta com a MESMA chave de antes. Se a preocupação era a chave ter vazado, inscreva o notebook de novo em vez de devolver.'}
      </p>

      <label>
        {cortando ? 'Por que este notebook está sendo cortado?' : 'Por que o acesso está voltando?'}
        <input
          className="campo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={cortando ? 'Computador roubado na secretaria' : 'Computador apareceu'}
          maxLength={500}
          required
        />
      </label>

      <label>
        Sua senha
        <input
          className="campo"
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="current-password"
          required
        />
      </label>

      <CaixaDoCodigo valor={codigo} aoMudar={setCodigo} desabilitada={indo} />

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button
          type="submit"
          className={cortando ? 'perigo' : undefined}
          disabled={indo || motivo.trim().length < 3 || senha.length === 0 || codigo.trim().length < 6}
        >
          {indo ? 'Mudando...' : cortando ? 'Cortar o acesso' : 'Devolver o acesso'}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>
          Deixar como está
        </button>
      </div>
    </form>
  )
}

/** De qual congregação o notebook novo vai falar. */
function FormularioDeInscrever({
  congregacoes,
  aoDesistir,
  aoGerar,
  aoSair
}: {
  congregacoes: { id: string; nome: string; e_sede: boolean }[]
  aoDesistir: () => void
  aoGerar: (codigo: CodigoDeInscricao) => void
  aoSair: () => void
}): JSX.Element {
  const [congregacaoId, setCongregacaoId] = useState(congregacoes[0]?.id ?? '')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function gerar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      aoGerar(await servidor.codigoDeInscricao(congregacaoId, senha, codigo.trim()))
    } catch (falha: unknown) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível gerar o código.')
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void gerar(e)}>
      <p className="explicacao">
        O código é digitado no notebook, na tela de inscrever, e vira a chave permanente dele.
        <strong> Escolher a congregação errada deixa o notebook isolado</strong>: ele passa a
        falar pela congregação errada e não recebe o que é dele.
      </p>

      <label>
        De qual congregação é o notebook?
        <select
          className="campo"
          value={congregacaoId}
          onChange={(e) => setCongregacaoId(e.target.value)}
          required
        >
          {congregacoes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}{c.e_sede ? ' (sede)' : ''}
            </option>
          ))}
        </select>
      </label>

      <label>
        Sua senha
        <input
          className="campo"
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="current-password"
          required
        />
      </label>

      <CaixaDoCodigo valor={codigo} aoMudar={setCodigo} desabilitada={indo} />

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button
          type="submit"
          disabled={indo || congregacaoId === '' || senha.length === 0 || codigo.trim().length < 6}
        >
          {indo ? 'Gerando...' : 'Gerar o código'}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>
          Desistir
        </button>
      </div>
    </form>
  )
}
